import type { ButtonProps } from '@bambi/component-library/src/components/Button';
import { Button } from '@bambi/component-library/src/components/Button';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { classNames } from '@bambi/component-library/src/utils/classNames';
import {
  CurrencyDollarIcon,
  EyeIcon,
  FolderArrowDownIcon,
  PaperAirplaneIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

type ManageCellItem = Omit<ButtonProps, 'children'> & {
  label: string;
  icon: 'pencil' | 'trash' | 'download' | 'eye' | 'currency' | 'send';
  iconClassName?: string;
  tooltip?: string;
};

interface ManageCellProps {
  items: ManageCellItem[];
}

/**
 * A component that renders a list of manage cell items.
 */
export function ManageCell({ items }: ManageCellProps) {
  return (
    <div className="flex flex-row items-center justify-end">
      {items.map(({ tooltip, ...item }) => {
        const button = (
          <Button
            key={item.label}
            dataTestId={`manage-cell-${item.label}`}
            variant="icon"
            ariaLabel={item.label}
            onClick={item.onClick}
            {...item}
          >
            <div
              className="flex flex-col items-center text-xs"
              style={{ fontSize: '0.625rem' }}
            >
              <ManageCellIcon
                icon={item.icon}
                iconClassName={item.iconClassName}
              />
              {item.label}
            </div>
          </Button>
        );

        if (!tooltip) {
          return button;
        }

        // The span lets hover events reach the tooltip when the button is
        // disabled, which kills its own pointer events
        return (
          <SimpleTooltip
            key={item.label}
            trigger={<span>{button}</span>}
            content={tooltip}
          />
        );
      })}
    </div>
  );
}

const manageCellIconMap = {
  pencil: PencilIcon,
  trash: TrashIcon,
  download: FolderArrowDownIcon,
  eye: EyeIcon,
  currency: CurrencyDollarIcon,
  send: PaperAirplaneIcon,
};

function ManageCellIcon({
  icon,
  iconClassName,
}: {
  icon: ManageCellItem['icon'];
  iconClassName?: string;
}) {
  const Icon = manageCellIconMap[icon];
  return (
    <Icon className={classNames('mt-1 h-[20px] w-[24px]', iconClassName)} />
  );
}
